import { useEffect, useMemo } from 'react';

import { IRestaurantNode } from 'generated/rbi-graphql';
import { useConfigValue } from 'hooks/configs/use-config-value';
import useGoogleGeolocationLibrary from 'hooks/geolocation/use-google-geolocation-library';
import { IUseMapHook, MarkerTypes } from 'hooks/use-map/types';
import { useStoreOpenStatusGenerator } from 'hooks/use-service-mode-status';
import { useGeolocation } from 'state/geolocation';
import { useLocale } from 'state/intl';
import { useServiceModeContext } from 'state/service-mode';
import { isValidPosition } from 'utils/geolocation';
import {
  getDateNowByTimezone,
  isMobileOrderingAvailable,
  isRestaurantOpen,
} from 'utils/restaurant';
import { isCatering } from 'utils/service-mode';

// TODO: RN - properly type these props or remove props
export interface IUseMarkersProps {
  createMarker: ReturnType<IUseMapHook>['createMarker'];
  panTo: ReturnType<IUseMapHook>['panTo'];
  onPress: (store: IRestaurantNode) => any;
  storesNearby?: IRestaurantNode[];
  storesFavs?: IRestaurantNode[];
  activeStoreId?: string | null;
  /**
   * Opt in to the larger branded pin for the selected store (red when open, black when
   * closed) instead of the legacy dot treatment. Used by the v2 store locator UX.
   */
  useSelectedStorePin?: boolean;
  /**
   * Opt in to reading open/closed from the store's available pickup service modes, the same
   * question the v2 locator card asks, so a pin and its card always agree. The v1 locator
   * keeps its own dining-room-or-drive-thru check.
   */
  useServiceModeOpenState?: boolean;
}

// TODO: RN - Rename to `useStoreMapMarkers` as this only is to track markers for the nearby stores
export default function useMarkers({
  createMarker,
  panTo,
  onPress,
  storesNearby,
  storesFavs,
  activeStoreId,
  useSelectedStorePin = false,
  useServiceModeOpenState = false,
}: IUseMarkersProps) {
  const { libraryLoaded: libLoaded } = useGoogleGeolocationLibrary();
  const { activeCoordinates } = useGeolocation();
  const { region } = useLocale();
  const { serviceMode } = useServiceModeContext();

  const restaurantsConfig = useConfigValue({ key: 'restaurants', defaultValue: {} });
  const validMobileOrderingEnvs = useMemo(
    () => restaurantsConfig.validMobileOrderingEnvs ?? [],
    [restaurantsConfig.validMobileOrderingEnvs]
  );

  const { generateOpenStatusForStore } = useStoreOpenStatusGenerator();

  const storeFavsMap = useMemo(() => new Set(storesFavs?.map(x => x._id)), [storesFavs]);

  useEffect(() => {
    if (!libLoaded) {
      return;
    }

    (storesNearby ?? []).forEach(store => {
      const id = store._id;

      if (!id) {
        return;
      }

      const location = { lat: store.latitude, lng: store.longitude };
      if (!isValidPosition(location)) {
        return;
      }

      const dateNow = getDateNowByTimezone({
        timeZone: store.timezone || undefined,
      });

      const diningRoomOpen = isRestaurantOpen(store.diningRoomHours, dateNow);
      const driveThruOpen = isRestaurantOpen(store.driveThruHours, dateNow);
      const open = driveThruOpen || diningRoomOpen;

      const isCorrectRegion =
        store &&
        store.physicalAddress &&
        store.physicalAddress.country &&
        store.physicalAddress.country.toUpperCase().startsWith(region);

      let storeMarkerType = MarkerTypes.StoreActive;

      if (id === activeStoreId) {
        storeMarkerType = MarkerTypes.StoreInActive;
      }

      const isStoreFavorite = storeFavsMap.has(id);
      if (isStoreFavorite && id !== activeStoreId) {
        storeMarkerType = MarkerTypes.StoreFavOpen;
      }

      const isCateringServiceMode = isCatering(serviceMode);
      const isStoreAvailable = useServiceModeOpenState
        ? generateOpenStatusForStore(store).isOpen
        : isCateringServiceMode ||
          (isMobileOrderingAvailable(store, validMobileOrderingEnvs) && open && isCorrectRegion);

      if (!isStoreAvailable) {
        if (isStoreFavorite) {
          storeMarkerType = MarkerTypes.StoreFavClosed;
        } else {
          storeMarkerType = MarkerTypes.StoreDisabled;
        }
      }

      // Selection is resolved last so the pin wins over the open/closed and favorite dots.
      if (useSelectedStorePin && id === activeStoreId) {
        storeMarkerType = isStoreAvailable
          ? MarkerTypes.StoreSelectedOpen
          : MarkerTypes.StoreSelectedClosed;
      }

      createMarker({
        id,
        type: storeMarkerType,
        location,
        title: store.name ?? undefined,
        onPress: () => {
          panTo(location);
          onPress(store);
        },
      });
    });
  }, [
    activeStoreId,
    createMarker,
    libLoaded,
    onPress,
    region,
    serviceMode,
    storeFavsMap,
    storesNearby,
    validMobileOrderingEnvs,
    panTo,
    useSelectedStorePin,
    useServiceModeOpenState,
    generateOpenStatusForStore,
  ]);

  // Create a marker for the users coordinates
  useEffect(() => {
    if (libLoaded && activeCoordinates) {
      createMarker({ type: MarkerTypes.User, location: activeCoordinates });
    }
  }, [activeCoordinates, createMarker, libLoaded]);
}
